import React, { Suspense } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Button,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ModerationFlagsList } from "./ModerationFlagsList";
import { ConfigModal } from "./ConfigModal";
import { useModerationApi } from "./hooks/useModerationApi";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";

interface ManagementContentProps {
  onBack: () => void;
}

const ManagementContent: React.FC<ManagementContentProps> = ({ onBack }) => {
  const [configDialogOpen, setConfigDialogOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const api = useModerationApi();

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    api
      .refreshFlags()
      .then(() => {
        setSnackbar({
          open: true,
          message: "Moderation flags refreshed successfully",
          severity: "success",
        });
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to refresh";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const handleOpenConfig = () => {
    setConfigDialogOpen(true);
  };

  const handleCloseConfig = () => {
    setConfigDialogOpen(false);
  };

  const handleConfigSubmit = () => {
    // Refresh data after config changes
    handleRefresh();
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <IconButton
          onClick={onBack}
          color="primary"
          sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
          title="Back to Video Platform"
        >
          <ArrowBackIcon />
        </IconButton>

        <IconButton
          onClick={handleRefresh}
          disabled={isRefreshing}
          color="primary"
          sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
          title="Refresh"
        >
          <RefreshIcon />
        </IconButton>

        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          onClick={handleOpenConfig}
          sx={{
            backgroundColor: "#7357FF",
            "&:hover": { backgroundColor: "#5a42cc" },
          }}
        >
          Configuration
        </Button>
      </Box>

      <ModerationFlagsList onRefresh={handleRefresh} />

      <Suspense
        fallback={
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <ConfigModal
          open={configDialogOpen}
          onClose={handleCloseConfig}
          onSubmit={handleConfigSubmit}
        />
      </Suspense>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

interface ModerationManagementProps {
  onBack?: () => void;
}

export const Management: React.FC<ModerationManagementProps> = ({ onBack }) => {
  // Default onBack handler if not provided
  const defaultOnBack = () => {
    // Navigate back to video platform - this will be handled by the parent
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem("activeScreenId", "videoPlatform");
      window.location.reload();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Chat Moderation
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Manage chat moderation flags and configuration
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Suspense
          fallback={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 400,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <ManagementContent onBack={onBack || defaultOnBack} />
        </Suspense>
      </Box>
    </Box>
  );
};
